import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Badge,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  School,
  Assessment,
  Security,
  Person,
  Settings,
  Logout,
  Notifications,
  Business,
  Group,
  BarChart,
  ExpandLess,
  ExpandMore,
  AdminPanelSettings,
  ManageAccounts,
  Analytics,
  LocationOn,
  CloudUpload,
  TableChart,
  Assignment,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [managementMenuOpen, setManagementMenuOpen] = useState(false);
  const [analyticsMenuOpen, setAnalyticsMenuOpen] = useState(false);
  const [adminMenuAnchor, setAdminMenuAnchor] = useState<null | HTMLElement>(null);
  const [managementMenuAnchor, setManagementMenuAnchor] = useState<null | HTMLElement>(null);
  const [analyticsMenuAnchor, setAnalyticsMenuAnchor] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  const handleProfile = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleSettings = () => {
    handleMenuClose();
    navigate('/settings');
  };

  // Menús organizados por categorías
  const menuStructure = {
    main: [
    {
      text: 'Mis Cursos',
      icon: <Dashboard />,
      path: '/dashboard',
      roles: ['admin', 'company_admin', 'employee'],
    },
    {
      text: 'Cursos',
      icon: <School />,
      path: '/courses',
      roles: ['admin', 'company_admin', 'employee'],
    },
    ],
    administration: {
      title: 'Administración',
      icon: <AdminPanelSettings />,
      roles: ['admin'],
      items: [
        {
          text: 'Cursos',
      icon: <School />,
      path: '/admin/courses',
    },
    {
          text: 'Evaluaciones',
      icon: <Assessment />,
      path: '/admin/evaluations',
    },
    {
          text: 'Empresas',
      icon: <Business />,
      path: '/admin/companies',
        },
    {
          text: 'Empleados',
      icon: <Group />,
      path: '/admin/employees',
        },
    {
          text: 'Oportunidades Comerciales',
      icon: <Business />,
      path: '/admin/domain-requests-grouped',
        },
    {
          text: 'Resultados',
      icon: <TableChart />,
      path: '/admin/all-results',
        },
      ],
    },
    management: {
      title: 'Gestión',
      icon: <ManageAccounts />,
      roles: ['admin', 'company_admin'],
      items: [
    {
          text: 'Empleados',
      icon: <Group />,
          path: '/company/employees',
    },
    {
          text: 'Cursos Pendientes',
      icon: <Assignment />,
          path: '/company/employees-pending-courses',
    },
    {
          text: 'Departamentos',
      icon: <Business />,
          path: '/company/departments',
    },
    {
          text: 'Sedes',
      icon: <LocationOn />,
          path: '/company/offices',
    },
    {
          text: 'Carga Masiva de Empleados',
      icon: <CloudUpload />,
          path: '/company/bulk-upload',
    },
    {
          text: 'Carga Masiva de Departamentos',
      icon: <CloudUpload />,
          path: '/company/bulk-upload-departments',
    },
    {
          text: 'Carga Masiva de Sedes',
      icon: <CloudUpload />,
          path: '/company/bulk-upload-offices',
    },
    {
          text: 'Certificados',
      icon: <School />,
          path: '/company/certificates',
    },
    {
          text: 'Preguntas de Cursos',
      icon: <Assessment />,
          path: '/company/evaluations',
    },
      ],
    },
    analytics: {
      title: 'Analíticas',
      icon: <Analytics />,
      roles: ['admin', 'company_admin'],
      items: [
    {
      text: 'Avance por Departamento',
      icon: <BarChart />,
      path: '/company/department-progress',
      roles: ['company_admin'],
    },
    {
      text: 'Avance por Sede',
      icon: <BarChart />,
      path: '/company/office-progress',
      roles: ['company_admin'],
    },
    {
      text: 'Resultados Completos',
      icon: <TableChart />,
      path: '/company/all-results',
      roles: ['company_admin', 'admin'],
    },
    {
      text: 'Empresas',
      icon: <Business />,
      path: '/admin/analytics/companies',
      roles: ['admin'],
    },
      ],
    },
  };

  const renderDesktopMenu = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Menú principal */}
      {menuStructure.main
        .filter(item => item.roles.includes(user?.role || ''))
        .map((item) => (
          <Button
            key={item.path}
            color="inherit"
            startIcon={item.icon}
            onClick={() => navigate(item.path)}
            sx={{
              backgroundColor: location.pathname === item.path ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
            }}
          >
            {item.text}
          </Button>
        ))}

             {/* Menú de Administración */}
       {menuStructure.administration.roles.includes(user?.role || '') && (
         <Box>
           <Button
             color="inherit"
             startIcon={menuStructure.administration.icon}
             endIcon={adminMenuOpen ? <ExpandLess /> : <ExpandMore />}
             onClick={(e) => {
               setAdminMenuAnchor(e.currentTarget);
               setAdminMenuOpen(!adminMenuOpen);
             }}
             sx={{
               backgroundColor: location.pathname.startsWith('/admin/') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
             }}
           >
             {menuStructure.administration.title}
           </Button>
           <Menu
             anchorEl={adminMenuAnchor}
             open={adminMenuOpen}
             onClose={() => setAdminMenuOpen(false)}
             anchorOrigin={{
               vertical: 'bottom',
               horizontal: 'left',
             }}
             transformOrigin={{
               vertical: 'top',
               horizontal: 'left',
             }}
           >
             {menuStructure.administration.items.map((item) => (
               <MenuItem
                 key={item.path}
                 onClick={() => {
                   navigate(item.path);
                   setAdminMenuOpen(false);
                 }}
                 selected={location.pathname === item.path}
               >
                 <ListItemIcon>{item.icon}</ListItemIcon>
                 {item.text}
               </MenuItem>
             ))}
           </Menu>
         </Box>
       )}

             {/* Menú de Gestión */}
       {menuStructure.management.roles.includes(user?.role || '') && (
         <Box>
           <Button
             color="inherit"
             startIcon={menuStructure.management.icon}
             endIcon={managementMenuOpen ? <ExpandLess /> : <ExpandMore />}
             onClick={(e) => {
               setManagementMenuAnchor(e.currentTarget);
               setManagementMenuOpen(!managementMenuOpen);
             }}
             sx={{
               backgroundColor: ['/company/employees', '/company/departments'].includes(location.pathname) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
             }}
           >
             {menuStructure.management.title}
           </Button>
           <Menu
             anchorEl={managementMenuAnchor}
             open={managementMenuOpen}
             onClose={() => setManagementMenuOpen(false)}
             anchorOrigin={{
               vertical: 'bottom',
               horizontal: 'left',
             }}
             transformOrigin={{
               vertical: 'top',
               horizontal: 'left',
             }}
           >
             {menuStructure.management.items.map((item) => (
               <MenuItem
                 key={item.path}
                 onClick={() => {
                   navigate(item.path);
                   setManagementMenuOpen(false);
                 }}
                 selected={location.pathname === item.path}
               >
                 <ListItemIcon>{item.icon}</ListItemIcon>
                 {item.text}
               </MenuItem>
             ))}
           </Menu>
         </Box>
       )}

             {/* Menú de Analíticas */}
       {menuStructure.analytics.roles.includes(user?.role || '') && (
         <Box>
           <Button
             color="inherit"
             startIcon={menuStructure.analytics.icon}
             endIcon={analyticsMenuOpen ? <ExpandLess /> : <ExpandMore />}
             onClick={(e) => {
               setAnalyticsMenuAnchor(e.currentTarget);
               setAnalyticsMenuOpen(!analyticsMenuOpen);
             }}
             sx={{
               backgroundColor: ['/company/dashboard', '/companies'].includes(location.pathname) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
             }}
           >
             {menuStructure.analytics.title}
           </Button>
           <Menu
             anchorEl={analyticsMenuAnchor}
             open={analyticsMenuOpen}
             onClose={() => setAnalyticsMenuOpen(false)}
             anchorOrigin={{
               vertical: 'bottom',
               horizontal: 'left',
             }}
             transformOrigin={{
               vertical: 'top',
               horizontal: 'left',
             }}
           >
             {menuStructure.analytics.items
               .filter(item => !item.roles || item.roles.includes(user?.role || ''))
               .map((item) => (
                 <MenuItem
                   key={item.path}
                   onClick={() => {
                     navigate(item.path);
                     setAnalyticsMenuOpen(false);
                   }}
                   selected={location.pathname === item.path}
                 >
                   <ListItemIcon>{item.icon}</ListItemIcon>
                   {item.text}
                 </MenuItem>
               ))}
           </Menu>
         </Box>
       )}
    </Box>
  );

  const renderMobileMenu = () => (
    <List>
      {/* Menú principal */}
      {menuStructure.main
        .filter(item => item.roles.includes(user?.role || ''))
        .map((item) => (
        <ListItem key={item.path} disablePadding>
          <ListItemButton
            onClick={() => {
              navigate(item.path);
              setMobileMenuOpen(false);
            }}
            selected={location.pathname === item.path}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        </ListItem>
      ))}

      <Divider />

      {/* Menú de Administración */}
      {menuStructure.administration.roles.includes(user?.role || '') && (
        <>
          <ListItem disablePadding>
            <ListItemButton onClick={() => setAdminMenuOpen(!adminMenuOpen)}>
              <ListItemIcon>{menuStructure.administration.icon}</ListItemIcon>
              <ListItemText primary={menuStructure.administration.title} />
              {adminMenuOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          <Collapse in={adminMenuOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {menuStructure.administration.items.map((item) => (
                <ListItemButton
                  key={item.path}
                  sx={{ pl: 4 }}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  selected={location.pathname === item.path}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </>
      )}

      {/* Menú de Gestión */}
      {menuStructure.management.roles.includes(user?.role || '') && (
        <>
          <ListItem disablePadding>
            <ListItemButton onClick={() => setManagementMenuOpen(!managementMenuOpen)}>
              <ListItemIcon>{menuStructure.management.icon}</ListItemIcon>
              <ListItemText primary={menuStructure.management.title} />
              {managementMenuOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          <Collapse in={managementMenuOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {menuStructure.management.items.map((item) => (
                <ListItemButton
                  key={item.path}
                  sx={{ pl: 4 }}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  selected={location.pathname === item.path}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </>
      )}

      {/* Menú de Analíticas */}
      {menuStructure.analytics.roles.includes(user?.role || '') && (
        <>
          <ListItem disablePadding>
            <ListItemButton onClick={() => setAnalyticsMenuOpen(!analyticsMenuOpen)}>
              <ListItemIcon>{menuStructure.analytics.icon}</ListItemIcon>
              <ListItemText primary={menuStructure.analytics.title} />
              {analyticsMenuOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          <Collapse in={analyticsMenuOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {menuStructure.analytics.items
                .filter(item => !item.roles || item.roles.includes(user?.role || ''))
                .map((item) => (
                  <ListItemButton
                    key={item.path}
                    sx={{ pl: 4 }}
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    selected={location.pathname === item.path}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                ))}
            </List>
          </Collapse>
        </>
      )}
    </List>
  );

  if (!user) return null;

  return (
    <>
      <AppBar position="static" elevation={1}>
        <Toolbar>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setMobileMenuOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            🛡️ Vigilantium E-Learning
          </Typography>

          {!isMobile && renderDesktopMenu()}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
            {/* Mostrar indicadores solo para admin y company_admin */}
            {user.role !== 'employee' && (
              <IconButton color="inherit">
                <Badge badgeContent={user.vulnerabilities?.filter(v => !v.resolved).length || 0} color="error">
                  <Notifications />
                </Badge>
              </IconButton>
            )}

            <IconButton
              onClick={handleMenuOpen}
              sx={{ ml: 1 }}
            >
              <Avatar
                sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}
              >
                {user.firstName.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Menú de usuario */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem disabled>
          <Box>
            <Typography variant="subtitle2">
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user.email}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              {user.company?.name || 'Empresa no asignada'}
            </Typography>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleProfile}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          Perfil
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Cerrar Sesión
        </MenuItem>
      </Menu>

      {/* Menú móvil */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      >
        <Box sx={{ width: 250, pt: 2 }}>
          <Box sx={{ px: 2, pb: 2 }}>
            <Typography variant="h6" gutterBottom>
              🛡️ Vigilantium
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user.company?.name || 'Empresa no asignada'}
            </Typography>
          </Box>
          <Divider />
          {renderMobileMenu()}
          <Divider />
                     <List>
             <ListItem disablePadding>
               <ListItemButton onClick={handleProfile}>
                 <ListItemIcon>
                   <Person />
                 </ListItemIcon>
                 <ListItemText primary="Perfil" />
               </ListItemButton>
             </ListItem>
             <ListItem disablePadding>
               <ListItemButton onClick={handleLogout}>
                 <ListItemIcon>
                   <Logout />
                 </ListItemIcon>
                 <ListItemText primary="Cerrar Sesión" />
               </ListItemButton>
             </ListItem>
           </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Header; 